import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  Globe,
  Smartphone,
  Server,
  Box,
  ArrowUpRight,
  RefreshCcw,
  ListTodo,
  Sparkles,
  Activity,
} from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CATEGORY_ICONS = {
  website: Globe,
  mobile_app: Smartphone,
  saas: Server,
  other: Box,
};

const CATEGORY_LABELS = {
  website: "Website",
  mobile_app: "Mobile App",
  saas: "SaaS",
  other: "Other",
};

const KpiCard = ({ label, value, icon: Icon, accent = false, testId }) => (
  <div
    data-testid={testId}
    className={`regal-card p-6 relative ${accent ? "tracing-beam" : ""}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="label-eyebrow">{label}</p>
        <p className="font-sans text-4xl font-light tracking-tight mt-3 text-textmain">
          {value}
        </p>
      </div>
      <div className="p-2 rounded-md border border-seam bg-elevated">
        <Icon className="w-4 h-4 text-champagne" strokeWidth={1.5} />
      </div>
    </div>
  </div>
);

const AddAppDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState("website");
  const [description, setDescription] = useState("");
  const [tech, setTech] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setName("");
    setUrl("");
    setCategory("website");
    setDescription("");
    setTech("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/apps", {
        name,
        url,
        category,
        description,
        tech_stack: tech
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      toast.success(`${data.name} added to your estate.`);
      reset();
      onCreated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create app");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="add-app-dialog"
        className="bg-surface border-seam max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl text-textmain">
            Add a project
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div>
            <label className="block label-eyebrow mb-2">Name</label>
            <input
              type="text"
              required
              data-testid="add-app-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 text-textmain focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
              placeholder="RoadRunner App"
            />
          </div>
          <div>
            <label className="block label-eyebrow mb-2">URL (optional)</label>
            <input
              type="text"
              data-testid="add-app-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 text-textmain focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
              placeholder="https://roadrunner.example.com"
            />
          </div>
          <div>
            <label className="block label-eyebrow mb-2">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                data-testid="add-app-category"
                className="bg-obsidian border-seam text-textmain"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-seam text-textmain">
                <SelectItem value="website">Website</SelectItem>
                <SelectItem value="mobile_app">Mobile App</SelectItem>
                <SelectItem value="saas">SaaS</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block label-eyebrow mb-2">Tech stack (comma separated)</label>
            <input
              type="text"
              data-testid="add-app-tech"
              value={tech}
              onChange={(e) => setTech(e.target.value)}
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 text-textmain focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
              placeholder="React, FastAPI, MongoDB"
            />
          </div>
          <div>
            <label className="block label-eyebrow mb-2">Short description</label>
            <textarea
              data-testid="add-app-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 text-textmain focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 resize-none"
              placeholder="A 24/7 roadside assistance dispatch app."
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-5 py-2.5 rounded-md border border-seam text-textmuted hover:text-textmain hover:border-champagne/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="add-app-submit"
              className="px-6 py-2.5 rounded-md bg-champagne text-obsidian font-medium hover:bg-champagne-soft transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)] disabled:opacity-60 inline-flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Add project
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AppCard = ({ app, onCheck }) => {
  const Icon = CATEGORY_ICONS[app.category] || Box;
  const [checking, setChecking] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!app.url) {
      toast.error("Add a URL first to enable health checks.");
      return;
    }
    setChecking(true);
    try {
      await onCheck(app.id);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Link
      to={`/apps/${app.id}`}
      data-testid={`app-card-${app.id}`}
      className="group regal-card p-6 hover:border-champagne/40 transition-all duration-300 relative block"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 rounded-md border border-seam bg-elevated">
            <Icon className="w-5 h-5 text-champagne" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <h3 className="font-heading text-2xl text-textmain truncate group-hover:text-champagne transition-colors">
              {app.name}
            </h3>
            <p className="text-xs text-textmuted truncate">
              {app.url || CATEGORY_LABELS[app.category] || "Untitled"}
            </p>
          </div>
        </div>
        <ArrowUpRight
          className="w-4 h-4 text-textmuted group-hover:text-champagne transition-colors mt-1"
          strokeWidth={1.5}
        />
      </div>

      {app.description ? (
        <p className="text-sm text-textmuted leading-relaxed mt-5 line-clamp-2">
          {app.description}
        </p>
      ) : (
        <p className="text-sm text-textmuted/60 italic mt-5">No description yet.</p>
      )}

      {app.tech_stack?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-5">
          {app.tech_stack.slice(0, 4).map((t) => (
            <span
              key={t}
              className="text-[10px] tracking-wider uppercase px-2 py-1 rounded-full border border-seam text-textmuted bg-elevated"
            >
              {t}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-6 pt-5 border-t border-seam">
        <StatusBadge status={app.status} />
        <button
          type="button"
          onClick={run}
          disabled={checking}
          data-testid={`app-card-check-${app.id}`}
          className="inline-flex items-center gap-2 text-xs text-textmuted hover:text-champagne transition-colors"
        >
          {checking ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
          )}
          {app.last_response_ms != null ? `${app.last_response_ms}ms` : "Ping"}
        </button>
      </div>
    </Link>
  );
};

const Dashboard = () => {
  const [apps, setApps] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [a, o] = await Promise.all([api.get("/apps"), api.get("/overview")]);
      setApps(a.data);
      setOverview(o.data);
    } catch (err) {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCheck = async (appId) => {
    try {
      const { data } = await api.post(`/apps/${appId}/check`);
      setApps((prev) =>
        prev.map((a) =>
          a.id === appId
            ? {
                ...a,
                status: data.status,
                last_response_ms: data.response_ms,
                last_checked_at: data.checked_at,
              }
            : a,
        ),
      );
      toast.success(
        data.status === "healthy"
          ? `Healthy · ${data.response_ms}ms`
          : `Down (${data.status_code || "no response"})`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Check failed");
    }
  };

  return (
    <Layout>
      <section className="mb-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="label-eyebrow">Your Estate</p>
            <h1 className="font-heading text-5xl sm:text-6xl tracking-tight mt-3">
              The control room.
            </h1>
            <p className="text-textmuted mt-4 leading-relaxed">
              Every app and website you steward, one quiet glance away. Teach the
              AI concierge how each one operates — it will return the favour.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            data-testid="add-app-btn"
            className="inline-flex items-center gap-2 self-start lg:self-auto bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-6 py-3 transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)]"
          >
            <Plus className="w-4 h-4" strokeWidth={1.8} />
            Add a project
          </button>
        </div>
      </section>

      <section
        data-testid="kpi-grid"
        className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12"
      >
        <KpiCard
          testId="kpi-total"
          label="Projects"
          value={overview?.total_apps ?? "—"}
          icon={Box}
          accent
        />
        <KpiCard
          testId="kpi-healthy"
          label="Healthy"
          value={overview?.healthy ?? "—"}
          icon={Activity}
        />
        <KpiCard
          testId="kpi-tasks"
          label="Open tasks"
          value={overview?.open_tasks ?? "—"}
          icon={ListTodo}
        />
        <KpiCard
          testId="kpi-insights"
          label="AI insights"
          value={overview?.insights ?? "—"}
          icon={Sparkles}
        />
      </section>

      <section>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-3xl tracking-tight">Projects</h2>
          <span className="text-xs text-textmuted">
            {apps.length} project{apps.length === 1 ? "" : "s"}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="dashboard-loading">
            <Loader2 className="w-6 h-6 text-champagne animate-spin" />
          </div>
        ) : apps.length === 0 ? (
          <div
            data-testid="empty-state"
            className="regal-card p-16 text-center crown-noise"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-champagne/30 bg-elevated mb-6">
              <Plus className="w-5 h-5 text-champagne" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-3xl mb-3">An empty estate</h3>
            <p className="text-textmuted max-w-md mx-auto mb-6">
              Begin by adding your first website or app. You can teach the AI
              concierge anything it should know about how to run it.
            </p>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              data-testid="empty-state-add-btn"
              className="inline-flex items-center gap-2 bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-6 py-3 transition-all"
            >
              <Plus className="w-4 h-4" strokeWidth={1.8} />
              Add your first project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {apps.map((a) => (
              <AppCard key={a.id} app={a} onCheck={handleCheck} />
            ))}
          </div>
        )}
      </section>

      <AddAppDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={(app) => {
          setApps((p) => [app, ...p]);
          refresh();
          navigate(`/apps/${app.id}`);
        }}
      />
    </Layout>
  );
};

export default Dashboard;
